/**
 * IndicatorsTab — Indicadores previsionales: UF, UTM, IMM y topes AFP/Salud/Cesantía.
 * Listado mensual con opción de carga manual y sincronización desde mindicador.cl
 */
import { useEffect, useState } from 'react'
import { AlertTriangle, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import {
  useIndicators, useCreateIndicator, PayrollIndicator,
  useIndicatorLegalDefaults,
  useUpdatePayrollIndicator, useDeletePayrollIndicator,
  useFamilyAllowanceBrackets, useCreateFamilyAllowanceBracket,
  useUpdateFamilyAllowanceBracket, useDeleteFamilyAllowanceBracket,
  type FamilyAllowanceBracket,
} from '../hooks/usePayroll'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { useAuthStore } from '@/store/authStore'
import { ToastContainer, useToast } from '@/components/Toast'
import { PeriodIndicatorsPanel } from './PeriodIndicatorsPanel'
import { Modal } from '@/components/ds/Modal'
import { extractFieldErrors, extractErrorMessage } from '@/lib/errorUtils'
import { useForm } from 'react-hook-form'
import { EmptyState } from '@/components/EmptyState'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

function useSyncIndicators() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const r = await apiClient.post('/payroll/indicators/sync/')
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-indicators'] }),
  })
}

interface IndicatorForm {
  year: number
  month: number
  uf_value: string
  utm_value: string
  imm_value: string
  tope_afp_uf: string
  tope_salud_uf: string
  tope_cesantia_uf: string
}

const currentYear = new Date().getFullYear()

function IndicatorModal({ initial, onClose }: { initial?: PayrollIndicator | null; onClose: () => void }) {
  const toast = useToast()
  const isEdit = !!initial?.id
  const { register, handleSubmit, setError, watch, setValue, formState: { errors, isSubmitting } } = useForm<IndicatorForm>({
    defaultValues: {
      year: initial?.year ?? currentYear,
      month: initial?.month ?? new Date().getMonth() + 1,
      uf_value: initial?.uf_value ?? '',
      utm_value: initial?.utm_value ?? '',
      imm_value: initial?.imm_value ?? '',
      // Los topes se prellenan con el valor LEGAL vigente del período elegido
      // (efecto de abajo), no con constantes en el front: las que había aquí
      // (82.0 / 82.0 / 122.6) eran topes de ~2022 y quien no las pisara a mano
      // dejaba el indicador peor que con el default del modelo.
      tope_afp_uf: initial?.tope_afp_uf ?? '',
      tope_salud_uf: initial?.tope_salud_uf ?? '',
      tope_cesantia_uf: initial?.tope_cesantia_uf ?? '',
    },
  })

  const watchedYear = Number(watch('year'))
  const watchedMonth = Number(watch('month'))
  const { data: legal } = useIndicatorLegalDefaults(watchedYear, watchedMonth)

  // Solo en el alta: al editar respetamos lo que el usuario ya tenía cargado.
  useEffect(() => {
    if (isEdit || !legal?.defaults) return
    const d = legal.defaults
    if (d.tope_afp_uf) setValue('tope_afp_uf', d.tope_afp_uf)
    if (d.tope_salud_uf) setValue('tope_salud_uf', d.tope_salud_uf)
    if (d.tope_cesantia_uf) setValue('tope_cesantia_uf', d.tope_cesantia_uf)
    if (d.imm_value) setValue('imm_value', d.imm_value)
  }, [legal, isEdit, setValue])

  const createIndicator = useCreateIndicator()
  const updateIndicator = useUpdatePayrollIndicator(initial?.id || '')

  const onSubmit = async (data: IndicatorForm) => {
    try {
      if (isEdit) await updateIndicator.mutateAsync(data as any)
      else await createIndicator.mutateAsync(data as any)
      toast.success('Indicador guardado')
      onClose()
    } catch (e: any) {
      const fields = extractFieldErrors(e)
      if (fields) {
        Object.entries(fields).forEach(([k, v]) => setError(k as any, { message: v }))
      } else {
        toast.error(extractErrorMessage(e, 'Error al guardar indicador'))
      }
    }
  }

  return (
    <Modal open title={isEdit ? 'Editar Indicadores' : 'Registrar Indicadores'} onClose={onClose} size="sm"
      actions={
        <>
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-divider hover:bg-soft">Cancelar</button>
          <button type="submit" form="indicator-form" disabled={isSubmitting} className="px-4 py-2 text-sm rounded-lg bg-primary text-on-primary hover:bg-primary-hover disabled:opacity-50">
            {isSubmitting ? 'Guardando...' : 'Guardar'}
          </button>
        </>
      }
    >
      <form id="indicator-form" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <TextField
            dense
            label="Año *"
            disabled={isEdit}
            inputProps={{ ...register('year', { required: 'Requerido', valueAsNumber: true }), type: 'number', min: '2020', max: '2030' }}
            error={errors.year?.message}
          />
          <SelectField
            dense
            label="Mes *"
            disabled={isEdit}
            selectProps={register('month', { valueAsNumber: true })}
            error={errors.month?.message}
          >
            {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </SelectField>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <TextField
            dense
            label="UF (CLP) *"
            placeholder="38000.00"
            inputProps={{ ...register('uf_value', { required: 'Requerido' }), type: 'number', step: '0.01' }}
            error={errors.uf_value?.message}
          />
          <TextField
            dense
            label="UTM (CLP) *"
            placeholder="65000"
            inputProps={{ ...register('utm_value', { required: 'Requerido' }), type: 'number', step: '1' }}
            error={errors.utm_value?.message}
          />
          <TextField
            dense
            label="IMM (CLP) *"
            placeholder="500000"
            inputProps={{ ...register('imm_value', { required: 'Requerido' }), type: 'number', step: '1' }}
            error={errors.imm_value?.message}
          />
        </div>

        <p className="text-xs font-semibold text-content-muted uppercase tracking-wider">Topes previsionales (en UF)</p>
        <div className="grid grid-cols-3 gap-4">
          <TextField
            dense
            label="Tope AFP"
            inputProps={{ ...register('tope_afp_uf'), type: 'number', step: '0.1' }}
          />
          <TextField
            dense
            label="Tope Salud"
            inputProps={{ ...register('tope_salud_uf'), type: 'number', step: '0.1' }}
          />
          <TextField
            dense
            label="Tope Cesantía"
            inputProps={{ ...register('tope_cesantia_uf'), type: 'number', step: '0.1' }}
          />
        </div>
      </form>
    </Modal>
  )
}

function fmt(val: string) {
  return Number(val).toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

export default function IndicatorsTab() {
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<PayrollIndicator | null>(null)
  const { data, isLoading } = useIndicators()
  const syncIndicators = useSyncIndicators()
  const del = useDeletePayrollIndicator()
  const { user } = useAuthStore()
  const toast = useToast()
  // La carga y edición de indicadores pasa a ser de la PLATAFORMA: son valores
  // legales idénticos para todas las empresas de Chile, así que mantenerlos por
  // tenant solo multiplica las oportunidades de que uno quede viejo — que es
  // exactamente cómo julio 2026 se calculó con topes de 2024. El cliente los ve
  // en `PeriodIndicatorsPanel`, en solo lectura.
  // `can()` no sirve para distinguir acá: devuelve true para el platform admin,
  // pero también para el cliente con permiso de edición en el módulo.
  const isPlatformAdmin = !!user?.is_platform_admin
  const canEdit = isPlatformAdmin

  const indicators: PayrollIndicator[] = data?.indicators ?? []

  const handleSync = async () => {
    try {
      await syncIndicators.mutateAsync()
      toast.success('Indicadores sincronizados desde mindicador.cl')
    } catch (e: any) {
      toast.error(extractErrorMessage(e, 'Error al sincronizar indicadores'))
    }
  }

  const handleDelete = async (ind: PayrollIndicator) => {
    if (!confirm(`¿Eliminar el indicador de ${MONTHS[(ind.month ?? 1) - 1]} ${ind.year}?`)) return
    try { await del.mutateAsync(ind.id); toast.success('Indicador eliminado') }
    catch (e: any) { toast.error(extractErrorMessage(e, 'Error al eliminar')) }
  }

  return (
    <>
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />

      <PeriodIndicatorsPanel />

      {/* Mantenimiento de los valores: solo para la plataforma. Para el cliente la
          pestaña termina en el panel de arriba. */}
      {isPlatformAdmin && (
      <div className="mt-10 pt-6 border-t border-divider">
      <div className="flex justify-between items-center mb-4">
        <div>
          <p className="text-sm font-bold text-content">
            Mantenimiento de indicadores <span className="text-content-subtle font-normal">· plataforma</span>
          </p>
          <p className="text-xs text-content-muted">
            {indicators.length} período{indicators.length !== 1 ? 's' : ''} cargado{indicators.length !== 1 ? 's' : ''} en este tenant.
            Los topes, el SIS y el IMM se resuelven del valor legal al calcular, aunque acá figuren otros.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSync} disabled={syncIndicators.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-divider rounded-lg hover:bg-soft disabled:opacity-50">
            <RefreshCw size={14} className={syncIndicators.isPending ? 'animate-spin' : ''} />
            Sincronizar
          </button>
          <button onClick={() => { setEditing(null); setShowModal(true) }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
            <Plus size={15} /> Registrar
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-content-subtle">Cargando...</div>
      ) : indicators.length === 0 ? (
        <EmptyState
          title="No hay indicadores registrados."
          hint="Se sincroniza desde mindicador.cl o se ingresa manualmente."
          size="md"
        />
      ) : (
        <DataTable<any>
          columns={[
            { key: 'period', header: 'Período', sortable: true,
              sortValue: ind => `${ind.year}-${String(ind.month ?? 1).padStart(2, '0')}`,
              render: ind => (
                <span className="font-medium text-content">
                  {MONTHS[(ind.month ?? 1) - 1]} {ind.year}
                </span>
              ) },
            { key: 'uf_value', header: 'UF', numeric: true, sortable: true,
              sortValue: ind => Number(ind.uf_value), render: ind => fmt(ind.uf_value) },
            { key: 'utm_value', header: 'UTM', numeric: true, sortable: true,
              sortValue: ind => Number(ind.utm_value), render: ind => fmt(ind.utm_value) },
            { key: 'imm_value', header: 'IMM', numeric: true, sortable: true,
              sortValue: ind => Number(ind.imm_value), render: ind => fmt(ind.imm_value) },
            { key: 'tope_afp_uf', header: 'Tope AFP (UF)', numeric: true, sortable: true,
              sortValue: ind => Number(ind.tope_afp_uf),
              render: ind => <span className="text-content-muted">{ind.tope_afp_uf}</span> },
            { key: 'tope_salud_uf', header: 'Tope Salud (UF)', numeric: true, sortable: true,
              sortValue: ind => Number(ind.tope_salud_uf),
              render: ind => <span className="text-content-muted">{ind.tope_salud_uf}</span> },
            { key: 'tope_cesantia_uf', header: 'Tope Ces. (UF)', numeric: true, sortable: true,
              sortValue: ind => Number(ind.tope_cesantia_uf),
              render: ind => <span className="text-content-muted">{ind.tope_cesantia_uf}</span> },
            ...(canEdit ? [{
              key: 'acciones', header: '', width: 88,
              render: (ind: any) => (
                <span className="whitespace-nowrap">
                  <button onClick={() => { setEditing(ind); setShowModal(true) }} className="p-1.5 rounded-lg hover:bg-soft text-content-subtle" title="Editar"><Pencil size={14} /></button>
                  <button onClick={() => handleDelete(ind)} className="p-1.5 rounded-lg hover:bg-danger-surface text-content-subtle hover:text-danger-content-soft" title="Eliminar"><Trash2 size={14} /></button>
                </span>
              ),
            }] : []),
          ]}
          rows={indicators}
          rowKey={ind => ind.id}
          minWidth={980}
          caption="Indicadores previsionales por período"
        />
      )}

      {showModal && <IndicatorModal initial={editing} onClose={() => { setShowModal(false); setEditing(null) }} />}

      <FamilyAllowanceBracketsSection />
      </div>
      )}
    </>
  )
}

// ─── Asignación Familiar: tramos editables ──────────────────────────────────

interface BracketForm {
  valid_from: string
  tramo: string
  income_from: string
  income_to: string
  amount: string
}

function BracketModal({ initial, onClose }: { initial?: FamilyAllowanceBracket | null; onClose: () => void }) {
  const toast = useToast()
  const isEdit = !!initial?.id
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<BracketForm>({
    defaultValues: {
      valid_from: initial?.valid_from || `${currentYear}-01-01`,
      tramo: initial?.tramo || 'A',
      income_from: initial?.income_from ?? '0',
      income_to: initial?.income_to ?? '',
      amount: initial?.amount ?? '0',
    },
  })
  const create = useCreateFamilyAllowanceBracket()
  const update = useUpdateFamilyAllowanceBracket(initial?.id || '')

  const onSubmit = async (data: BracketForm) => {
    const payload: any = {
      valid_from: data.valid_from,
      tramo: data.tramo,
      income_from: data.income_from || '0',
      income_to: data.income_to === '' ? null : data.income_to,
      amount: data.amount || '0',
    }
    try {
      if (isEdit) await update.mutateAsync(payload)
      else await create.mutateAsync(payload)
      toast.success('Tramo guardado')
      onClose()
    } catch (e: any) {
      toast.error(extractErrorMessage(e, 'Error al guardar el tramo'))
    }
  }

  return (
    <Modal open title={isEdit ? 'Editar tramo' : 'Nuevo tramo'} onClose={onClose} size="sm"
      actions={
        <>
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-divider hover:bg-soft">Cancelar</button>
          <button type="submit" form="bracket-form" disabled={isSubmitting} className="px-4 py-2 text-sm rounded-lg bg-primary text-on-primary hover:bg-primary-hover disabled:opacity-50">
            {isSubmitting ? 'Guardando...' : 'Guardar'}
          </button>
        </>
      }
    >
      <form id="bracket-form" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <TextField
            dense
            label="Vigente desde *"
            inputProps={{ ...register('valid_from', { required: 'Requerido' }), type: 'date' }}
            error={errors.valid_from?.message}
          />
          <SelectField
            dense
            label="Tramo *"
            selectProps={register('tramo', { required: true })}
          >
            {['A','B','C','D'].map(t => <option key={t} value={t}>Tramo {t}</option>)}
          </SelectField>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <TextField
            dense
            label="Renta desde"
            placeholder="0"
            inputProps={{ ...register('income_from'), type: 'number', step: '1' }}
          />
          <TextField
            dense
            label="Renta hasta"
            placeholder="(sin tope)"
            inputProps={{ ...register('income_to'), type: 'number', step: '1' }}
          />
          <TextField
            dense
            label="Monto por carga *"
            placeholder="22007"
            inputProps={{ ...register('amount', { required: 'Requerido' }), type: 'number', step: '1' }}
            error={errors.amount?.message}
          />
        </div>
        <p className="text-xs text-content-muted">
          El tramo aplica si la renta del trabajador es mayor que «desde» y menor o igual que «hasta».
          Deja «hasta» vacío para el tramo sin tope (D).
        </p>
      </form>
    </Modal>
  )
}

function FamilyAllowanceBracketsSection() {
  const { data, isLoading } = useFamilyAllowanceBrackets()
  const del = useDeleteFamilyAllowanceBracket()
  const { user } = useAuthStore()
  const toast = useToast()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<FamilyAllowanceBracket | null>(null)
  // Mismo criterio que los indicadores: los tramos los fija la ley (se reajustan
  // en la misma proporción que el IMM), así que los mantiene la plataforma. El
  // cliente los ve por período en `PeriodIndicatorsPanel`.
  const canEdit = !!user?.is_platform_admin

  const brackets: FamilyAllowanceBracket[] = data?.brackets ?? []

  const handleDelete = async (b: FamilyAllowanceBracket) => {
    if (!confirm(`¿Eliminar el tramo ${b.tramo} vigente desde ${b.valid_from}?`)) return
    try { await del.mutateAsync(b.id); toast.success('Tramo eliminado') }
    catch (e: any) { toast.error(extractErrorMessage(e, 'Error al eliminar')) }
  }

  return (
    <div className="mt-10">
      <div className="flex justify-between items-center mb-2">
        <div>
          <h3 className="text-sm font-bold text-content">Asignación Familiar — Tramos</h3>
          <p className="text-xs text-content-muted">
            Monto por carga simple según el tramo de renta del trabajador. Se muestran
            todas las vigencias: la actual queda marcada y las anteriores se conservan
            para poder explicar un monto de un mes pasado.
          </p>
        </div>
        {canEdit && (
          <button onClick={() => { setEditing(null); setModalOpen(true) }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
            <Plus size={15} /> Nuevo tramo
          </button>
        )}
      </div>

      {canEdit && (
        <div className="mb-3 flex gap-2 rounded-lg border border-warning-border-strong bg-warning-surface px-3 py-2 text-xs text-warning-content-strong">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Estos tramos son <strong>nacionales</strong>: los fija un decreto y viven a
            nivel de plataforma, así que un cambio acá <strong>afecta a todas las
            empresas del país</strong>, no solo a esta. Los períodos ya calculados no
            se modifican.
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-8 text-content-subtle text-sm">Cargando...</div>
      ) : brackets.length === 0 ? (
        <EmptyState title="No hay tramos cargados." size="sm" />
      ) : (
        <DataTable<any>
          columns={[
            { key: 'valid_from', header: 'Vigente desde', sortable: true,
              render: b => (
                <span className="inline-flex items-center gap-2 text-content">
                  {b.valid_from}
                  {b.is_current && (
                    <span className="rounded-full bg-success-surface-strong px-2 py-0.5 text-[10px] font-semibold text-success-content">
                      Vigente
                    </span>
                  )}
                </span>
              ) },
            { key: 'tramo', header: 'Tramo', sortable: true,
              sortValue: b => b.tramo_display || `Tramo ${b.tramo}`,
              render: b => (
                <span className="font-medium text-content">{b.tramo_display || `Tramo ${b.tramo}`}</span>
              ) },
            { key: 'income_from', header: 'Renta desde', numeric: true, sortable: true,
              sortValue: b => Number(b.income_from),
              render: b => <span className="text-content-muted">{fmt(b.income_from)}</span> },
            { key: 'income_to', header: 'Renta hasta', numeric: true, sortable: true,
              // El tramo sin tope va al final suba o baje: no es «el mayor», es
              // la ausencia de límite.
              sortValue: b => (b.income_to ? Number(b.income_to) : null),
              render: b => (
                <span className="text-content-muted">{b.income_to ? fmt(b.income_to) : 'Sin tope'}</span>
              ) },
            { key: 'amount', header: 'Monto por carga', numeric: true, sortable: true,
              sortValue: b => Number(b.amount),
              render: b => <span className="font-medium text-content">${fmt(b.amount)}</span> },
            ...(canEdit ? [{
              key: 'acciones', header: '', width: 88,
              render: (b: any) => (
                <span className="whitespace-nowrap">
                  <button onClick={() => { setEditing(b); setModalOpen(true) }} className="p-1.5 rounded-lg hover:bg-soft text-content-subtle" title="Editar"><Pencil size={14} /></button>
                  <button onClick={() => handleDelete(b)} className="p-1.5 rounded-lg hover:bg-danger-surface text-content-subtle hover:text-danger-content-soft" title="Eliminar"><Trash2 size={14} /></button>
                </span>
              ),
            }] : []),
          ]}
          rows={brackets}
          rowKey={b => b.id}
          minWidth={840}
          // El tramo vigente se destaca y los históricos se atenúan ENTEROS:
          // es el dato que se busca al abrir esta pantalla.
          rowClassName={b => (b.is_current ? 'bg-primary-50/40 dark:bg-primary-900/10' : 'opacity-70')}
          caption="Tramos de asignación familiar por vigencia"
        />
      )}

      {modalOpen && <BracketModal initial={editing} onClose={() => { setModalOpen(false); setEditing(null) }} />}
    </div>
  )
}
